import { StreemAPI } from '@streem/api';
import { invariant, retryWithBackoff } from '@streem/toolbox';
import { map, mergeMap, shareReplay, startWith } from 'rxjs/operators';
import { logger } from '../../util/logger';
import { participantFilterProvider } from '../../util/rx/participant_filter_provider';
import { lastCreatedAt } from '../../util/rx/last_created_at';
import { accumulateAndMapWallEvents, filterByProperty, findInGroup, mapWallItemFromWallItemEvents, } from '../wall/utils';
export class RoomController {
    constructor(config) {
        this.config = config;
        this.room = this.config.roomWall.itemsById.pipe(findInGroup(this.config.context.roomId.toString()), mapWallItemFromWallItemEvents('room'));
        this.roomStatus = this.room.pipe(map(room => room?.status ?? undefined));
        this.participantUpdates = this.room.pipe(mergeMap(room => room?.participants ?? []));
        this.localParticipant = this.participantUpdates.pipe(participantFilterProvider(this.config.context.localUserId));
        this.localParticipantStatus = this.localParticipant.pipe(map(p => p.status || undefined));
        this.remoteParticipant = this.participantUpdates.pipe(participantFilterProvider(this.config.context.remoteUserId));
        this.remoteParticipantStatus = this.remoteParticipant.pipe(map(p => p.status || undefined));
        this.remoteParticipantCapabilities = this.config.roomWall.itemsByType.pipe(findInGroup('capabilities'), accumulateAndMapWallEvents('capabilities'), filterByProperty('ownerUserId', this.config.context.remoteUserId?.toString()), lastCreatedAt());
        this.onsite = this.config.roomWall.itemsByType.pipe(findInGroup('roomInvitation'), accumulateAndMapWallEvents('roomInvitation'), map(invitations => invitations.length === 0), startWith(true), shareReplay(1));
    }
    async leaveRoom() {
        const maybeError = await this.callLeaveRoomEndpoint();
        if (maybeError instanceof Error) {
            logger.error(`${this.config.context.localUserId} failed to leave room ${this.config.context.roomId}.`, maybeError);
        }
    }
    async requeue(companyCode, groupName, reservationSid, transferToGroupName) {
        const requeueResultOrError = await this.callRequeueEndpointWithRetry(companyCode, groupName, reservationSid, transferToGroupName);
        if (requeueResultOrError instanceof Error) {
            logger.error('Exhaused attempts to requeue', {
                errorMessage: requeueResultOrError.message,
                stack: requeueResultOrError.stack,
            });
            return false;
        }
        else {
            logger.debug('successfully requeued:', {
                companyCode,
                groupName,
                reservationSid,
                transferToGroupName,
            });
            return true;
        }
    }
    async sendClientCapabilities(canSendCameraSubjects) {
        invariant(this.config.context.callSettings, 'Call settings must be defined to send client capabilities');
        const capabilities = {
            canReceiveCompressedDataTrackMessages: true,
            canReceiveNonUprightCameraImages: false,
            sendsArScanningRequests: true,
            canReceiveVideo: true,
            canSendCameraSubjects,
        };
        logger.info(` ${this.config.context.localUserId} has sent their capabilities`, capabilities);
        this.config.roomWall.addCapabilities(capabilities);
    }
    async callLeaveRoomEndpoint() {
        invariant(this.config.context.localUserId, 'Expected localUserId to be defined when leaving room');
        try {
            await StreemAPI.rooms.leaveRoom(this.config.context.roomId.toString(), this.config.context.localUserId.toString());
            logger.info(`User ${this.config.context.localUserId} has left room ${this.config.context.roomId}`);
        }
        catch (e) {
            if (e.status === 304) {
                logger.info(`${this.config.context.localUserId} has already left the room. Ignoring API request to leave`);
            }
            else if (e instanceof Error) {
                return e;
            }
            else {
                // e is not a standard Error object
                const err = new Error('failed to call leaveRoomEndpoint');
                err.cause = e;
                return err;
            }
        }
    }
    async callRequeueEndpointWithRetry(companyCode, groupName, reservationSid, transferToGroupName) {
        function requeueApiCall() {
            return StreemAPI.companies.transferGroupReservationToRequeue(companyCode, groupName, reservationSid, { transferToGroupName });
        }
        function onFailedAttempt(attempt, error) {
            logger.info('Retrying requeue', { attempt, errorMessage: error.message });
        }
        return await retryWithBackoff(requeueApiCall, 3, onFailedAttempt);
    }
}
//# sourceMappingURL=room_controller.js.map